import { useCallback, useEffect, useRef, useState } from 'react'
import {
	EventCandidateRunStart,
	EventCandidateRunStatus,
	getEventCandidateRunStatus,
	runEventCandidateJob,
} from 'src/apis/eventCandidateAPI'
import { useModal } from 'src/context/ModalContext'
import {
	dailyLimitNotice,
	getApiErrorMessage,
	humanizeCode,
	toTimestamp,
} from './eventCandidateRun'

export const RUN_POLL_MS = 10000
// After a start, how many checks may still show the run before it.
const START_GRACE_CHECKS = 3

interface PendingStart {
	previousStartedAt: number | null
	checksLeft: number
}

/**
 * Starts the event finder and follows the latest run.
 * The status is read once on mount, then every 10 seconds while a run is in
 * progress. `onFinished` fires when a run that was being followed ends.
 */
export function useEventCandidateRun(onFinished: () => void) {
	const { openError, openModal } = useModal()
	const [status, setStatus] = useState<EventCandidateRunStatus | null>(null)
	const [statusError, setStatusError] = useState('')
	// False until the first status check has answered or failed.
	const [ready, setReady] = useState(false)
	const [starting, setStarting] = useState(false)
	// A run was started here and the status does not show it yet.
	const [waiting, setWaiting] = useState(false)
	// A run ended while this page was following it.
	const [finishedHere, setFinishedHere] = useState(false)

	const mountedRef = useRef(false)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const sequenceRef = useRef(0)
	const followingRef = useRef(false)
	const startingRef = useRef(false)
	const pendingRef = useRef<PendingStart | null>(null)
	// undefined until a status check has succeeded.
	const lastStartedAtRef = useRef<number | null | undefined>(undefined)
	const onFinishedRef = useRef(onFinished)
	onFinishedRef.current = onFinished
	const openModalRef = useRef(openModal)
	openModalRef.current = openModal
	const checkRef = useRef<() => Promise<void>>(async () => {})

	const clearTimer = useCallback(() => {
		if (timerRef.current) clearTimeout(timerRef.current)
		timerRef.current = null
	}, [])

	const checkStatus = useCallback(async () => {
		// Only the newest check may update the state or schedule the next one.
		const sequence = ++sequenceRef.current
		const isCurrent = () =>
			mountedRef.current && sequence === sequenceRef.current
		const checkAgainLater = () => {
			clearTimer()
			timerRef.current = setTimeout(() => checkRef.current(), RUN_POLL_MS)
		}
		clearTimer()
		try {
			const res = await getEventCandidateRunStatus()
			if (!isCurrent()) return
			const object = res?.data?.results?.object
			const next: EventCandidateRunStatus | null =
				object && typeof object === 'object' ? object : null
			const startedAt = toTimestamp(next?.started_at)
			const pending = pendingRef.current
			const showsOlderRun =
				pending && !next?.running && startedAt === pending.previousStartedAt

			setReady(true)
			if (showsOlderRun && pending.checksLeft > 0) {
				pending.checksLeft -= 1
				setStatusError('')
				checkAgainLater()
				return
			}
			pendingRef.current = null
			lastStartedAtRef.current = startedAt
			setWaiting(false)
			setStatus(next)
			if (showsOlderRun) {
				// Never present the earlier run as the result of this one.
				followingRef.current = false
				setStatusError(
					'The run was started, but its status has not appeared yet. Reload the page to check again.',
				)
				return
			}
			setStatusError('')
			if (next?.running) {
				followingRef.current = true
				checkAgainLater()
			} else if (followingRef.current) {
				followingRef.current = false
				setFinishedHere(true)
				const limitNotice = dailyLimitNotice(next)
				if (limitNotice) {
					openModalRef.current({ type: 'warning', label: limitNotice })
				}
				onFinishedRef.current()
			}
		} catch (error) {
			if (!isCurrent()) return
			setReady(true)
			setStatusError(getApiErrorMessage(error))
			// One failed check proves nothing about a run that was in progress.
			if (followingRef.current) checkAgainLater()
		}
	}, [clearTimer])
	checkRef.current = checkStatus

	useEffect(() => {
		mountedRef.current = true
		checkStatus()
		return () => {
			// Leaving the page stops the polling and drops any answer in flight.
			mountedRef.current = false
			sequenceRef.current += 1
			clearTimer()
		}
	}, [checkStatus, clearTimer])

	const start = useCallback(async () => {
		if (startingRef.current || followingRef.current) return
		startingRef.current = true
		setStarting(true)
		try {
			const res = await runEventCandidateJob()
			if (!mountedRef.current) return
			const result: EventCandidateRunStart = res?.data?.results?.object || {}
			if (result.started) {
				followingRef.current = true
				setFinishedHere(false)
				setWaiting(true)
				pendingRef.current =
					lastStartedAtRef.current === undefined
						? null
						: {
								previousStartedAt: lastStartedAtRef.current,
								checksLeft: START_GRACE_CHECKS,
						  }
				openModal({ type: 'success', label: 'Started looking for events' })
			} else if (result.reason === 'another_run_in_progress') {
				followingRef.current = true
				setFinishedHere(false)
				openModal({
					type: 'warning',
					label: 'A run is already in progress. Following that one.',
				})
			} else {
				openModal({
					type: 'warning',
					label: `The run did not start${
						result.reason ? `: ${humanizeCode(result.reason)}` : ''
					}`,
				})
			}
			await checkStatus()
		} catch (error) {
			openError(error)
		} finally {
			startingRef.current = false
			if (mountedRef.current) setStarting(false)
		}
	}, [checkStatus, openError, openModal])

	return {
		status,
		statusError,
		ready,
		starting,
		waiting,
		running: waiting || Boolean(status?.running),
		finishedHere,
		start,
		refresh: checkStatus,
	}
}
